import { useMemo, useState, useEffect } from 'react';
import { Timeline, Typography, Button } from 'antd';
import { SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons';
import { groupEventsByMonth, resolveApplicationDate } from '../../utils/timelineHelpers';
import DateSectionHeader from '../timeline/DateSectionHeader';
import ReferralEvent from '../timeline/events/ReferralEvent';
import ApplicationEvent from '../timeline/events/ApplicationEvent';
import SubmissionEvent from '../timeline/events/SubmissionEvent';
import { COLORS, SPACING } from '../../constants/theme';

const { Text } = Typography;

const CandidateActivityTimeline = ({ referrals }) => {
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .ant-timeline-item-head {
        background-color: transparent !important;
      }
      .ant-timeline-item-tail {
        border-left-color: ${COLORS.TIMELINE_LINE} !important;
        border-left-width: 2px !important;
      }
      .timeline-sort-button:active,
      .timeline-sort-button:focus,
      .timeline-sort-button:hover {
        background-color: ${COLORS.WHITE} !important;
        color: inherit !important;
        border-color: ${COLORS.GRAY_BORDER_DARK} !important;
      }
      .timeline-sort-button:active {
        background-color: ${COLORS.WHITE} !important;
        color: inherit !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const timelineEvents = useMemo(() => {
    if (!referrals || referrals.length === 0) return null;

    const events = [];

    referrals.forEach((referral) => {
      events.push({
        type: 'referral',
        date: referral.createdAt,
        referral,
        id: `referral-${referral.id}`,
      });

      if (referral.referralSubmissions && referral.referralSubmissions.length > 0) {
        referral.referralSubmissions.forEach((submission) => {
          events.push({
            type: 'referralSubmission',
            date: submission.referralDate,
            submission,
            referral,
            id: `submission-${submission.id}`,
          });
        });
      }

      if (referral.applications && referral.applications.length > 0) {
        referral.applications.forEach((application) => {
          events.push({
            type: 'application',
            date: resolveApplicationDate(application, referral.referralSubmissions),
            application,
            referral,
            id: `application-${application.id}`,
          });
        });
      }
    });

    return events.sort((a, b) => {
      if (sortOrder === 'desc') {
        return new Date(b.date) - new Date(a.date);
      } else {
        return new Date(a.date) - new Date(b.date);
      }
    });
  }, [referrals, sortOrder]);

  const renderTimelineItem = (event) => {
    switch (event.type) {
      case 'referral':
        return ReferralEvent({ referral: event.referral });
      case 'application':
        return ApplicationEvent({ application: event.application, date: event.date });
      case 'referralSubmission':
        return SubmissionEvent({ submission: event.submission, referral: event.referral });
      default:
        return null;
    }
  };

  if (!timelineEvents || timelineEvents.length === 0) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '60px 20px',
          backgroundColor: COLORS.GRAY_BG,
          borderRadius: '12px',
          margin: '20px 0',
        }}
      >
        <Text type="secondary" style={{ fontSize: '14px' }}>
          No activity found for this candidate
        </Text>
      </div>
    );
  }

  const groupedEvents = useMemo(() => groupEventsByMonth(timelineEvents), [timelineEvents]);

  return (
    <div style={{ padding: `${SPACING.SM} 0`, marginLeft: '20px', marginRight: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '28px' }}>
        <Button
          size="small"
          className="timeline-sort-button"
          icon={sortOrder === 'desc' ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
          onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
          style={{ borderRadius: '6px' }}
        >
          {sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
        </Button>
      </div>
      <Timeline style={{ marginTop: SPACING.SM, paddingLeft: '4px' }}>
        {groupedEvents.map((event) => {
          if (event.type === 'header') {
            return <DateSectionHeader key={event.id} date={event.date} />;
          }

          const item = renderTimelineItem(event);
          if (!item) return null;

          return (
            <Timeline.Item
              key={event.id}
              dot={item.dot}
              color={item.color}
            >
              {item.children}
            </Timeline.Item>
          );
        })}
      </Timeline>
    </div>
  );
};

export default CandidateActivityTimeline;
